import posthog from 'posthog-js';
import { browser } from '$app/environment';
import { PUBLIC_POSTHOG_KEY, PUBLIC_POSTHOG_HOST } from '$env/static/public';

let initialized = false;

/** Detect if running on a mobile device */
function isMobile(): boolean {
	if (!browser) return false;
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/** Core initialization logic - separated for sync/async usage */
function doInit() {
	if (initialized) return;

	const mobile = isMobile();

	posthog.init(PUBLIC_POSTHOG_KEY, {
		api_host: PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
		person_profiles: 'identified_only',
		capture_pageview: false, // We'll capture manually for SPA
		capture_pageleave: true,
		// Disable autocapture on mobile to reduce JS work
		autocapture: !mobile,
		persistence: 'localStorage+cookie',
		mask_all_text: false,
		mask_all_element_attributes: false,
		// Disable session recording on mobile (heavy rrweb library)
		disable_session_recording: mobile,
		// Reduce network requests on mobile
		request_batching: true
	});

	initialized = true;
}

/** Initialize PostHog analytics. Requires cookie consent and a valid API key. */
export function initPostHog() {
	if (!browser || initialized) return;

	const consent = localStorage.getItem('cookie-consent');
	if (consent !== 'accepted') return;

	if (!PUBLIC_POSTHOG_KEY) return;

	doInit();

	// Capture the initial pageview now that PostHog is ready
	posthog.capture('$pageview', { $current_url: window.location.href });
}

/** Capture a pageview event for SPA navigation tracking. */
export function capturePageview(url: string) {
	if (!browser || !initialized) return;
	posthog.capture('$pageview', { $current_url: url });
}

export interface AnalyticsEventMap {
	// Theme & UI
	theme_toggled: { theme: string };

	// Terminal
	terminal_opened: { source: string };
	terminal_closed: void;
	terminal_command_executed: { command: string; category: string };
	terminal_game_started: { game: string };
	terminal_game_ended: { game: string; score?: number; duration_seconds?: number };
	terminal_animation_started: { animation: string };
	terminal_tool_used: { tool: string };

	// Coding Challenge
	coding_challenge_started: void;
	coding_challenge_attempt: { challenge: number; success: boolean; attempts: number };
	coding_challenge_completed: { duration_seconds: number; total_attempts: number };
	coding_challenge_application_submitted: { duration_seconds: number };

	// Command Palette
	command_palette_opened: void;
	command_palette_executed: { command: string };

	// Contact Form
	contact_form_started: { lang: string };
	contact_form_field_focused: { field: string; lang: string };
	contact_form_submitted: { challenge?: string; has_company?: boolean; lang?: string };
	contact_form_error: { errors?: string[]; type?: string; lang?: string };
	contact_form_success: { challenge?: string; lang?: string };

	// Navigation & Links
	cta_clicked: { cta: string; page: string; lang: string };
	link_clicked: { url: string; type: 'internal' | 'external'; text?: string };
	nav_item_clicked: { item: string; lang: string };

	// Language
	language_switched: { from: string; to: string };

	// Content Engagement
	scroll_depth_reached: { depth: number; page: string };
	section_viewed: { section: string; page: string };
	service_detail_viewed: { service: string; lang: string };
	case_study_viewed: { case_study: string; lang: string };
	blog_article_clicked: { article: string; lang: string };

	// Consent
	cookie_consent: { action: string };
	cookie_consent_given: { action: string };

	// Performance
	web_vital: { name: string; value: number; rating: string; delta: number; id: string };

	// Session
	session_started: {
		referrer?: string;
		utm_source?: string;
		utm_medium?: string;
		utm_campaign?: string;
	};
}

export type AnalyticsEventName = keyof AnalyticsEventMap;

// Preserve backward-compatible union type for any code referencing AnalyticsEvent
export type AnalyticsEvent = {
	[K in AnalyticsEventName]: AnalyticsEventMap[K] extends void
		? { event: K }
		: { event: K; properties: AnalyticsEventMap[K] };
}[AnalyticsEventName];

/** Capture a custom analytics event with type-safe properties. */
export function captureEvent<K extends AnalyticsEventName>(
	...args: AnalyticsEventMap[K] extends void
		? [event: K]
		: [event: K, properties: AnalyticsEventMap[K]]
): void {
	if (!browser || !initialized) return;
	posthog.capture(args[0], args[1] as Record<string, unknown>);
}

/** Identify a user with a unique ID and optional properties. */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
	if (!browser || !initialized) return;
	posthog.identify(userId, properties);
}

/** Reset the current user identity (e.g. on logout). */
export function resetUser() {
	if (!browser || !initialized) return;
	posthog.reset();
}

/** Capture an exception/error event and send it to PostHog for monitoring. */
export function captureException(error: unknown, context?: Record<string, unknown>) {
	if (!browser || !initialized) return;
	const message = error instanceof Error ? error.message : String(error);
	const stack = error instanceof Error ? error.stack : undefined;
	posthog.capture('$exception', {
		$exception_message: message,
		$exception_stack: stack,
		...context
	});
}

// ─── Scroll Tracking ─────────────────────────────────────────────────────────

/** Scroll depth percentages to track for engagement analytics */
const SCROLL_DEPTH_MILESTONES = [25, 50, 75, 100] as const;

/** Maximum length for link text in analytics (prevents bloated payloads) */
const LINK_TEXT_MAX_LENGTH = 100;

/** Intersection threshold for section visibility (0.5 = 50% visible) */
const SECTION_VISIBILITY_THRESHOLD = 0.5;

const scrollMilestones = new Set<number>();
let scrollTrackingEnabled = false;

/** Initialize scroll depth tracking for the current page. */
export function initScrollTracking(page: string) {
	if (!browser) return;

	scrollMilestones.clear();
	scrollTrackingEnabled = true;

	const handleScroll = () => {
		if (!scrollTrackingEnabled) return;

		const scrollTop = window.scrollY;
		const docHeight = document.documentElement.scrollHeight - window.innerHeight;
		const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

		for (const milestone of SCROLL_DEPTH_MILESTONES) {
			if (scrollPercent >= milestone && !scrollMilestones.has(milestone)) {
				scrollMilestones.add(milestone);
				captureEvent('scroll_depth_reached', { depth: milestone, page });
			}
		}
	};

	window.addEventListener('scroll', handleScroll, { passive: true });

	return () => {
		scrollTrackingEnabled = false;
		window.removeEventListener('scroll', handleScroll);
	};
}

// ─── Link Tracking ───────────────────────────────────────────────────────────

/** Track a link click event. */
export function trackLinkClick(url: string, text?: string) {
	if (!browser || !initialized) return;

	const isExternal = url.startsWith('http') && !url.includes(window.location.hostname);
	captureEvent('link_clicked', {
		url,
		type: isExternal ? 'external' : 'internal',
		text: text?.slice(0, LINK_TEXT_MAX_LENGTH)
	});
}

/** Track a CTA button click. */
export function trackCTA(cta: string, page: string, lang: string) {
	captureEvent('cta_clicked', { cta, page, lang });
}

// ─── Session Tracking ────────────────────────────────────────────────────────

/** Capture session start with UTM parameters. */
export function captureSessionStart() {
	if (!browser || !initialized) return;

	const params = new URLSearchParams(window.location.search);
	captureEvent('session_started', {
		referrer: document.referrer || undefined,
		utm_source: params.get('utm_source') || undefined,
		utm_medium: params.get('utm_medium') || undefined,
		utm_campaign: params.get('utm_campaign') || undefined
	});
}

// ─── Section Visibility Tracking ─────────────────────────────────────────────

/** Create an intersection observer to track section visibility. */
export function createSectionObserver(page: string) {
	if (!browser) return null;

	const observedSections = new Set<string>();

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					const section = entry.target.getAttribute('data-track-section');
					if (section && !observedSections.has(section)) {
						observedSections.add(section);
						captureEvent('section_viewed', { section, page });
					}
				}
			}
		},
		{ threshold: SECTION_VISIBILITY_THRESHOLD }
	);

	return observer;
}
